import type { BuiltinAnswers, CustomAnswer, PublicEvent, PdfRevisionProgress } from '../../shared/types';
import { adultAgeOf, emptyBuiltin, validateContact, validateMedical, validateLimits, isAdult, isMinor } from '../../shared/builtin';
import { activeQuestionFields, activeQuestionAnswers, hasRouting } from '../../shared/questionRouting';
import { validateAnswer } from '../../shared/answers';
import { documentCompletionIssues } from './pdfState';
import { readPdfRevisionProgress } from '../../shared/pdfProgress';
import { isValidDate } from '../../shared/validate';
import { localTodayIso } from '../../shared/dates';
import { CONDUCT_VERSION, CONDUCT_ERROR } from '../../shared/conduct';
import type { SignatureInput } from '../lib/pdf';

export interface RegState {
  builtin: BuiltinAnswers;
  custom: Record<string, CustomAnswer>;
  step: number;
  questionSectionId?: string | null;
  conductAcknowledgment?: string;
  participantSig?: SignatureInput | null;
  parentSig?: SignatureInput | null;
  signatureContext?: string;
  /** Date captured when the permission form was signed; the reviewed and delivered copy uses it. */
  standardSignedOn?: string;
  pdfProgress?: PdfRevisionProgress;
  collectingEvent?: PublicEvent;
  recoveryNotice?: string;
  /** False when the participant won't attend: only their name, ward and message are sent. Unset, they're attending. */
  attending?: boolean;
  /** What a participant who won't attend shares with the organizer. */
  organizerMessage?: string;
  /** Sent with a decline, so sending it again finds the row the first one made. */
  declineAttemptId?: string;
}
// Preserve stage IDs for older tab backups; stage 4 is now part of Conditions, and stage 0, the event's details, is now
// at the top of every step.
export const STEPS = ['Event', 'Contact', 'Medical', 'Conditions', 'Other', 'Questions', 'Sign', 'Review', 'Conduct', 'Forms'] as const;
export function stepsFor(ev: PublicEvent): number[] {
  const hasCustom = ev.fields.some((f) => f.type !== 'section' && f.type !== 'title_text');
  return [1, 2, 3, ...(hasCustom ? [5] : []), ...(ev.documentForms?.length ? [9] : []), 8, 6, 7];
}
export function errorsFor(step: number, ev: PublicEvent, b: BuiltinAnswers, custom: Record<string, CustomAnswer>, conductAcknowledgment?: string, questionFields?: PublicEvent['fields']) {
  const builtin = step === 1 ? validateContact(b, todayIso(), ev.level === 'stake' ? ev.wardNames ?? [] : [], isAdult(b.dateOfBirth, todayIso(), adultAgeOf(ev)))
    : step === 2 ? validateMedical(b) : step === 3 ? validateLimits(b) : {};
  const questions: Record<string, string> = {};
  if (step === 5) for (const f of questionFields ?? activeQuestionFields(ev.fields, custom)) { const err = validateAnswer(f, custom[f.id]); if (err) questions[f.id] = err; }
  if (step === 8 && conductAcknowledgment !== CONDUCT_VERSION) questions.conductAcknowledgment = CONDUCT_ERROR;
  return { builtin, questions };
}
/** Signatures are valid only for the answers and event information the signer saw. */
export function signatureContext(ev: PublicEvent, s: RegState): string {
  // Not the day: signatures made on an earlier day stay, and the form shows the day they were made (standardSignedOn).
  return JSON.stringify([{ ...ev, pdfSubmissionEnabled: undefined, submissionProtocols: undefined }, s.builtin, s.custom, s.conductAcknowledgment]);
}
export function resumeStep(ev: PublicEvent, s: RegState): number {
  const steps = stepsFor(ev);
  const requested = s.step === 4 ? 3 : steps.includes(s.step) ? s.step : steps[0];
  for (const step of steps.slice(0, steps.indexOf(requested))) {
    const e = errorsFor(step, ev, s.builtin, s.custom, s.conductAcknowledgment);
    if (Object.keys(e.builtin).length || Object.keys(e.questions).length) return step;
  }
  if (steps.indexOf(requested) > steps.indexOf(9) && steps.includes(9) && ev.documentForms?.some(document => documentCompletionIssues(ev, document, s).length)) return 9;
  if (requested === 7 && (!s.participantSig || (isMinor(s.builtin.dateOfBirth, s.standardSignedOn ?? todayIso(), adultAgeOf(ev)) && !s.parentSig) || s.signatureContext !== signatureContext(ev, s))) return 6;
  return requested;
}
/** Carry only shared household details into a fresh registration for this event. */
export function nextParticipantState(ev: PublicEvent, previous: BuiltinAnswers): RegState {
  return {
    builtin: {
      ...emptyBuiltin(),
      ward: ev.level === 'stake' && (ev.wardNames ?? []).includes(previous.ward) ? previous.ward : '',
      address: previous.address, city: previous.city, stateOrProvince: previous.stateOrProvince,
      emergencyContact: previous.emergencyContact, emergencyPrimaryPhone: previous.emergencyPrimaryPhone,
      emergencySecondaryPhone: previous.emergencySecondaryPhone,
    },
    custom: {}, step: 1,
  };
}
const key = (path: string) => `cpf:draft:${path}`;
function readSignature(value: unknown): SignatureInput | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as { kind?: string; name?: unknown; png?: unknown };
  if (v.kind === 'typed' && typeof v.name === 'string' && v.name.trim()) return { kind: 'typed', name: v.name };
  if (v.kind === 'drawn' && Array.isArray(v.png) && v.png.length && v.png.length < 2e6 && v.png.every(n => Number.isInteger(n) && n >= 0 && n <= 255)) return { kind: 'drawn', png: new Uint8Array(v.png) };
  return null;
}
/** Tab-scoped backup only; no participant draft is ever sent to a server. */
export function loadDraft(path: string): RegState {
  try {
    const raw = sessionStorage.getItem(key(path));
    if (raw) {
      const s = JSON.parse(raw);
      const builtin = emptyBuiltin();
      for (const k of Object.keys(builtin) as (keyof BuiltinAnswers)[]) {
        const v = s.builtin?.[k];
        if (typeof builtin[k] === 'string' ? typeof v === 'string' : v === null || typeof v === 'boolean') Object.assign(builtin, { [k]: v });
      }
      // Older drafts preselected No: those values cannot establish deliberate answers.
      if (s.schema !== 2) for (const k of ['specialDiet', 'allergies', 'chronicIllness', 'surgery'] as const) if (builtin[k] !== true) builtin[k] = null;
      return { builtin, custom: s.custom && typeof s.custom === 'object' && !Array.isArray(s.custom) ? s.custom : {}, step: Number.isInteger(s.step) ? s.step : 1,
        ...(s.questionSectionId === null || typeof s.questionSectionId === 'string' ? { questionSectionId: s.questionSectionId } : {}),
        conductAcknowledgment: s.conductAcknowledgment === CONDUCT_VERSION ? CONDUCT_VERSION : undefined,
        participantSig: readSignature(s.participantSig), parentSig: readSignature(s.parentSig), signatureContext: s.signatureContext, ...(typeof s.standardSignedOn === 'string' && isValidDate(s.standardSignedOn) ? { standardSignedOn: s.standardSignedOn } : {}), ...(s.pdfProgress !== undefined ? { pdfProgress: readPdfRevisionProgress(s.pdfProgress) } : {}),
        ...(typeof s.recoveryNotice === 'string' && s.recoveryNotice.length <= 2000 ? { recoveryNotice: s.recoveryNotice } : {}),
        ...(s.attending === false ? { attending: false } : {}), ...(typeof s.organizerMessage === 'string' ? { organizerMessage: s.organizerMessage.slice(0, 2000) } : {}),
        ...(typeof s.declineAttemptId === 'string' && /^[0-9a-f-]{36}$/i.test(s.declineAttemptId) ? { declineAttemptId: s.declineAttemptId } : {}),
        ...(s.collectingEvent && typeof s.collectingEvent === 'object' && s.collectingEvent.path === path && Array.isArray(s.collectingEvent.fields) && Array.isArray(s.collectingEvent.documentForms) ? { collectingEvent: s.collectingEvent } : {}) };
    }
  } catch { /* A damaged or unavailable backup should not block registration. */ }
  return { builtin: emptyBuiltin(), custom: {}, step: 1 };
}
export function saveDraft(path: string, s: RegState): boolean {
  const serialise = (sig?: SignatureInput | null) => sig?.kind === 'drawn' ? { kind: 'drawn', png: Array.from(sig.png) } : sig;
  try { sessionStorage.setItem(key(path), JSON.stringify({ ...s, schema: 2, participantSig: serialise(s.participantSig), parentSig: serialise(s.parentSig) })); return true; } catch { return false; }
}
export function clearDraft(path: string): void { try { sessionStorage.removeItem(key(path)); } catch { /* ignore */ } }
export const todayIso = localTodayIso;

/** A restored draft keeps only answers its routing still asks for, unless an attempt is already frozen. */
export function restoreActiveAnswers(ev: PublicEvent, draft: RegState): RegState {
  if (draft.pdfProgress?.submission || !hasRouting(ev.fields)) return draft;
  return { ...draft, custom: activeQuestionAnswers(ev.fields, draft.custom) };
}
